import * as React from 'react';

/**
 * Create and attach element to the body to be used for portal.
 * @private
 *
 * {@link https://www.jayfreestone.com/writing/react-portals-with-hooks}
 *
 * @param {Object} [options] Configuration for the portal
 * @returns {React.Ref} A ref to the portal element
 */
var usePortal = function usePortal() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var _options$a11yHidden = options.a11yHidden,
    a11yHidden = _options$a11yHidden === void 0 ? true : _options$a11yHidden;
  var portalRef = React.useRef(null);
  var container = document.body;
  if (!portalRef.current) {
    portalRef.current = document.createElement('div');
  }

  // istanbul ignore next
  React.useEffect(function () {
    // istanbul ignore next
    if (!a11yHidden) {
      return function () {
        return undefined;
      };
    }

    // We need to add `aria-hidden` to all children of <body>,
    // except for the modal. We .filter() the list to find only
    // <div>-like elements, ignoring <script>, <style>, etc.
    // We also don't want to affect any elements that may already
    // have `aria-hidden` set to true.

    var bodyChildren = Array.from(container.children).filter(function (child) {
      return child.innerHTML && [null, 'false'].includes(child.getAttribute('aria-hidden'));
    });
    bodyChildren.forEach(function (el) {
      return el.setAttribute('aria-hidden', 'true');
    });
    return function () {
      bodyChildren.forEach(function (el) {
        return el.removeAttribute('aria-hidden');
      });
    };
  }, [a11yHidden, container]);
  React.useEffect(function () {
    container.appendChild(portalRef.current);
    return function () {
      // istanbul ignore next
      if (!portalRef.current) {
        return;
      }

      // NOTE: IE 11 needs removeChild
      container.removeChild(portalRef.current);
    };
  }, [container, portalRef]);
  return portalRef;
};

export { usePortal };
